import { useParams } from 'react-router-dom'

import { Table, Text, colors } from '@factorialco/deprecated-design-system'
import {
  CreateFavouriteProjectDocument,
  CreateFavouriteProjectMutation,
  DeleteFavouriteProjectDocument,
  DeleteFavouriteProjectMutation,
  FavouriteProjectsDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FavouriteButton } from 'modules/project_management/components/FavouriteButton'
import { isOffDay } from 'modules/project_management/components/tables/MyProjectsTable/utils'
import {
  WIDTH_PROJECTS_COLUMN,
  WIDTH_WEEKDAYS_COLUMN,
} from 'modules/project_management/lib/constants'
import { getProjectSubprojectName } from 'modules/project_management/lib/formatText'
import { CustomGlobalTimeRecord } from 'modules/project_management/lib/getCustomGlobalTimeRecordsForMyProjectsTable'
import type ProjectsStore from 'modules/project_management/store/models/Projects'

import { ICurrentWeekHeaderInfo } from '../../_Header'
import AddEditCell from './AddEditCell'

interface RowProps {
  currentWeekHeaderInfo: ICurrentWeekHeaderInfo[]
  customGlobalTimeRecord: CustomGlobalTimeRecord
  temporaryRecords: CustomGlobalTimeRecord[]
  canRegisterFlexibleTracking: boolean
  projectsStore: ProjectsStore
}

const Row = ({
  currentWeekHeaderInfo,
  customGlobalTimeRecord,
  temporaryRecords,
  canRegisterFlexibleTracking,
  projectsStore,
}: RowProps) => {
  const { employeeId } = useParams()
  const favouriteProjectId = customGlobalTimeRecord.favouriteProjectId
  const refetchQueries = [
    {
      query: FavouriteProjectsDocument,
      variables: {
        employeeId: Number(employeeId),
      },
    },
  ]

  const [createFavouriteProject] = useMutation(CreateFavouriteProjectDocument, {
    extractorFn: (data: CreateFavouriteProjectMutation) =>
      data.projectManagementMutations.createProjectmanagementFavouriteProject,
    refetchQueries,
  })

  const [deleteFavouriteProject] = useMutation(DeleteFavouriteProjectDocument, {
    extractorFn: (data: DeleteFavouriteProjectMutation) =>
      data.projectManagementMutations.deleteProjectmanagementFavouriteProject,
    update(cache) {
      cache.evict({
        id: cache.identify({
          id: customGlobalTimeRecord.favouriteProjectId,
          __typename: 'ProjectManagementFavouriteProject',
        }),
      })
    },
  })

  const changeFavouriteProject = async (isFavourite: boolean) => {
    if (!isFavourite && favouriteProjectId) {
      const result = await deleteFavouriteProject({ variables: { favouriteProjectId } })
      const handle = handleResult(result, (errors) => {
        if (errors) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('errors.generic_error'),
          })
        }
      })

      if (handle.success) {
        track(trackingEvents.projectManagement.flexibleTracking.deleteFavouriteProject)
      }
    }

    if (isFavourite && customGlobalTimeRecord.projectWorkerId) {
      const result = await createFavouriteProject({
        variables: {
          projectWorkerId: customGlobalTimeRecord.projectWorkerId,
          subprojectId: customGlobalTimeRecord.subprojectId,
        },
      })
      const handle = handleResult(result, (errors) => {
        if (errors) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('errors.generic_error'),
          })
        }
      })

      if (handle.success) {
        track(trackingEvents.projectManagement.flexibleTracking.createFavouriteProject)
      }
    }
  }

  return (
    <Table.Row>
      <Table.Cell>
        <FavouriteButton
          disabled={!canRegisterFlexibleTracking}
          isFavourite={!!favouriteProjectId}
          onChange={changeFavouriteProject}
        />
      </Table.Cell>
      <Table.Cell colSpan={WIDTH_PROJECTS_COLUMN}>
        <Text.Normal weight="medium">
          {getProjectSubprojectName(
            `${customGlobalTimeRecord.projectName}`,
            customGlobalTimeRecord.subprojectName
          )}
        </Text.Normal>
      </Table.Cell>
      {customGlobalTimeRecord.imputedMinutesList.map((imputedMinutes, index) => (
        <Table.Cell
          key={index}
          colSpan={WIDTH_WEEKDAYS_COLUMN}
          paddingY="s6"
          paddingX="s8"
          style={
            isOffDay(index, currentWeekHeaderInfo) ? { backgroundColor: colors.grey100 } : undefined
          }
        >
          <AddEditCell
            weekDay={index}
            customGlobalTimeRecord={customGlobalTimeRecord}
            imputedMinutes={imputedMinutes.imputedMinutes}
            isFlexible={!!imputedMinutes.isFlexible}
            hasComment={!!imputedMinutes.comment}
            openClockInMinutes={imputedMinutes.openClockInMinutes}
            temporaryRecords={temporaryRecords}
            canRegisterFlexibleTracking={canRegisterFlexibleTracking}
            projectsStore={projectsStore}
          />
        </Table.Cell>
      ))}
    </Table.Row>
  )
}

export default Row
